// frontend/src/hooks/useEventSource.js
//
// Hook React pour consommer un flux Server-Sent Events avec reconnexion
// auto + replay implicite via Last-Event-ID.
//
// Conçu pour AuctionDetail.jsx (live bids), mais utilisable pour tout flux
// SSE futur (chat, notifications, etc.). EventSource est natif côté browser
// donc pas de dépendance externe — la complexité ici c'est :
//   - reconnect avec backoff exponentiel (le browser fait 3 s constant par
//     défaut ce qui est trop agressif sur une enchère qui rame)
//   - exposer le dernier event id au backend via la query string (sans
//     header custom puisqu'EventSource ne permet pas de set des headers ;
//     le navigateur envoie Last-Event-ID automatiquement à la reconnexion
//     mais SEULEMENT pour l'événement built-in, pas pour les events
//     `event: name\n` qu'on émet — voir
//     https://html.spec.whatwg.org/multipage/server-sent-events.html ; on
//     fait notre propre tracking en parallèle pour être robustes)
//   - cleanup propre au démontage (sinon le browser laisse traîner une
//     connexion fantôme jusqu'au GC)

import { useEffect, useRef, useState } from 'react';

const RECONNECT_INITIAL_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

export default function useEventSource(url, { onEvent, enabled = true } = {}) {
  const [status, setStatus] = useState('idle'); // 'idle' | 'connecting' | 'open' | 'closed'
  const lastEventIdRef = useRef(null);
  const reconnectDelayRef = useRef(RECONNECT_INITIAL_MS);
  const sourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const onEventRef = useRef(onEvent);

  // On stocke la callback dans une ref pour éviter de re-souscrire à
  // chaque render du parent (les pages re-créent leurs handlers inline).
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled || !url) {
      setStatus('idle');
      return undefined;
    }

    // EventSource n'existe pas dans jsdom (test env) ni dans certains
    // navigateurs très anciens. Pas de polyfill : on tombe en mode no-op
    // et le polling (3 min fallback dans AuctionDetail) couvre.
    if (typeof EventSource === 'undefined') {
      setStatus('idle');
      return undefined;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      setStatus('connecting');

      // On colle Last-Event-ID en query string parce qu'EventSource ne
      // permet pas de header custom (le navigateur le pose automatiquement
      // pour les events natifs, mais on veut un comportement déterministe
      // côté serveur). Le backend lit ?lastEventId=... OU le header.
      const sep = url.includes('?') ? '&' : '?';
      const finalUrl = lastEventIdRef.current
        ? `${url}${sep}lastEventId=${lastEventIdRef.current}`
        : url;

      const es = new EventSource(finalUrl);
      sourceRef.current = es;

      es.onopen = () => {
        if (cancelled) return;
        setStatus('open');
        reconnectDelayRef.current = RECONNECT_INITIAL_MS; // reset backoff
      };

      // Les events nommés (event: bid_placed\n) ne déclenchent PAS onmessage —
      // EventSource les délivre via addEventListener(type, handler). Comme
      // on ne connaît pas les noms à l'avance, on intercepte au niveau
      // "raw" en surchargeant l'EventSource. Plus simple : wildcard via
      // addEventListener('message', handler) ne marche que pour les events
      // sans event field. On opte pour l'écoute explicite des 2 types qu'on
      // sait émis par l'API (bid_placed, auction_closed). Pour étendre, le
      // backend doit publier un nouveau type ET le hook doit l'écouter ici.
      ['bid_placed', 'auction_closed'].forEach((eventType) => {
        es.addEventListener(eventType, (e) => {
          if (cancelled) return;
          lastEventIdRef.current = e.lastEventId || lastEventIdRef.current;
          let parsed;
          try {
            parsed = JSON.parse(e.data);
          } catch {
            parsed = e.data;
          }
          onEventRef.current?.({ type: eventType, data: parsed, id: e.lastEventId });
        });
      });

      es.onerror = () => {
        // EventSource passe en CONNECTING quand la connexion drop et tente
        // un reconnect automatique. Si readyState === CLOSED on a une
        // erreur fatale (404, 500…) — on prend la main pour faire notre
        // propre backoff au lieu de boucler à 3 s.
        if (es.readyState === EventSource.CLOSED) {
          es.close();
          sourceRef.current = null;
          if (cancelled) return;
          setStatus('closed');
          const delay = reconnectDelayRef.current;
          reconnectDelayRef.current = Math.min(delay * 2, RECONNECT_MAX_MS);
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      setStatus('closed');
    };
  }, [url, enabled]);

  return { status };
}
